import Routing, { type RouteName } from "routing";

import Uri from "@app:pages/common/vendor/jsuri-1.3.1";

import type {
  Absence,
  AbsenceType,
  EmployeeAvailabilityPage,
  HolidayBalance,
  ReplacementDraft,
  ReplacementSlot,
  ValidatedHoursNotice,
} from "../types";

type RequestOptions = { signal?: AbortSignal };

export interface ReplacementInput extends Omit<ReplacementDraft, "employee"> {
  replacedJobId: number;
}

export interface AbsenceInput {
  typeId?: number | null;
  sameCaseAbsenceId?: number | null;
  beginDate?: string | null;
  endDate?: string | null;
  beginTime?: string;
  endTime?: string;
  comment?: string;
  replacements?: ReplacementInput[];
}

export interface AvailabilityFilter {
  from: string;
  to: string;
  search?: string;
  page?: number;
  perPage?: number;
}

export interface AbsenceApi {
  fetchAbsences(
    employeeId: number,
    options?: RequestOptions,
  ): Promise<Absence[]>;
  fetchAbsence(absenceId: number, options?: RequestOptions): Promise<Absence>;
  createAbsence(
    employeeId: number,
    input: AbsenceInput,
    options?: RequestOptions,
  ): Promise<Absence>;
  updateAbsence(
    absenceId: number,
    input: AbsenceInput,
    options?: RequestOptions,
  ): Promise<Absence>;
  deleteAbsence(absenceId: number, options?: RequestOptions): Promise<void>;
  fetchAbsenceTypes(options?: RequestOptions): Promise<AbsenceType[]>;
  fetchReplacementSlots(
    employeeId: number,
    query: { absenceId?: number; from?: string; to?: string },
    options?: RequestOptions,
  ): Promise<ReplacementSlot[]>;
  fetchValidatedHours(
    employeeId: number,
    query: { absenceId?: number; from?: string; to?: string },
    options?: RequestOptions,
  ): Promise<ValidatedHoursNotice>;
  fetchHolidayBalance(
    employeeId: number,
    year?: number,
    options?: RequestOptions,
  ): Promise<HolidayBalance | null>;
  fetchAvailableEmployees(
    filter: AvailabilityFilter,
    options?: RequestOptions,
  ): Promise<EmployeeAvailabilityPage>;
}

type UriParams = Record<string, string | number | undefined>;

const generateUri = (routeName: RouteName, params: UriParams = {}): Uri => {
  const defined: Record<string, string | number> = {};

  for (const [key, value] of Object.entries(params)) {
    if (undefined !== value) {
      defined[key] = value;
    }
  }

  const uri = new Uri(Routing.generate(routeName, defined));

  const routeVariableNames = Routing.getVariableNames(routeName);

  for (const [key, value] of Object.entries(defined)) {
    // Already spent on the path
    if (routeVariableNames.includes(key)) {
      continue;
    }

    uri.replaceQueryParam(key, String(value));
  }

  return uri;
};

const JSON_HEADERS = { "Content-Type": "application/json" };

async function read<T>(uri: Uri, init?: RequestInit): Promise<T> {
  const response = await fetch(uri.toString(), init);

  if (!response.ok) {
    throw new Error(await failureMessage(response));
  }

  return response.json();
}

async function failureMessage(response: Response): Promise<string> {
  const payload = await response.json().catch(() => null);
  const detail =
    payload && typeof payload === "object" && "message" in payload
      ? String((payload as { message: unknown }).message)
      : null;

  return detail || response.statusText || `HTTP ${response.status}`;
}

export const absenceApi: AbsenceApi = {
  fetchAbsences(employeeId, options) {
    return read(
      generateUri("api_employee_absences_list", { employeeId }),
      options,
    );
  },

  fetchAbsence(absenceId, options) {
    return read(generateUri("api_absences_show", { absenceId }), options);
  },

  createAbsence(employeeId, input, options) {
    return read(generateUri("api_employee_absences_create", { employeeId }), {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify({ absenceInput: input }),
      ...options,
    });
  },

  updateAbsence(absenceId, input, options) {
    return read(generateUri("api_absences_update", { absenceId }), {
      method: "PUT",
      headers: JSON_HEADERS,
      body: JSON.stringify({ absenceInput: input }),
      ...options,
    });
  },

  async deleteAbsence(absenceId, options) {
    const uri = generateUri("api_absences_delete", { absenceId });

    const response = await fetch(uri.toString(), {
      method: "DELETE",
      ...options,
    });

    if (!response.ok) {
      throw new Error(await failureMessage(response));
    }
  },

  fetchAbsenceTypes(options) {
    return read(generateUri("api_absence_types_list"), options);
  },

  fetchReplacementSlots(employeeId, query, options) {
    return read(
      generateUri("api_employee_replacement_slots", { employeeId, ...query }),
      options,
    );
  },

  fetchValidatedHours(employeeId, query, options) {
    return read(
      generateUri("api_employee_validated_hours", { employeeId, ...query }),
      options,
    );
  },

  async fetchHolidayBalance(employeeId, year, options) {
    const uri = generateUri("api_employee_holiday_balance", {
      employeeId,
      year,
    });

    const response = await fetch(uri.toString(), options);

    if (!response.ok) {
      throw new Error(await failureMessage(response));
    }

    return 204 === response.status ? null : response.json();
  },

  fetchAvailableEmployees(filter, options) {
    return read(generateUri("api_employees_available", { ...filter }), options);
  },
};
